import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Calendar, Users, DollarSign, FileText, ShoppingBag } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DoctorDashboard = () => {
  const { user, getAuthHeader } = useAuth();
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completeDialog, setCompleteDialog] = useState(null);
  const [consultationDetail, setConsultationDetail] = useState(null);
  const [patientHistory, setPatientHistory] = useState([]);
  const [prescriptionNotes, setPrescriptionNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [doctorProfile, setDoctorProfile] = useState(null);
  const [loadingSuggestion, setLoadingSuggestion] = useState(false);

  useEffect(() => {
    fetchConsultations();
    fetchDoctorProfile();
  }, []);

  const fetchDoctorProfile = async () => {
    try {
      const res = await axios.get(`${API}/doctors/me`, { headers: getAuthHeader() });
      setDoctorProfile(res.data);
    } catch {
      // not doctor or no profile
    }
  };

  const handleCompleteConsultation = async () => {
    if (!completeDialog) return;
    setSubmitting(true);
    try {
      await axios.put(
        `${API}/consultations/${completeDialog.id}`,
        {
          prescription: { notes: prescriptionNotes, completed_at: new Date().toISOString() },
          status: 'completed',
        },
        { headers: getAuthHeader() }
      );
      toast.success('Consultation completed');
      setCompleteDialog(null);
      setPrescriptionNotes('');
      fetchConsultations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete consultation');
    } finally {
      setSubmitting(false);
    }
  };

  const fetchConsultations = async () => {
    try {
      const response = await axios.get(`${API}/consultations`, { headers: getAuthHeader() });
      setConsultations(response.data);
    } catch (error) {
      toast.error('Failed to load consultations');
    } finally {
      setLoading(false);
    }
  };

  const upcomingConsultations = consultations.filter((c) => c.status === 'scheduled');
  const completedConsultations = consultations.filter((c) => c.status === 'completed');

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="container mx-auto px-6 lg:px-12 py-12" data-testid="doctor-dashboard">
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
            Doctor Dashboard
          </h1>
          <p className="text-lg text-gray-600">Welcome, Dr. {user?.name}</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Calendar className="h-5 w-5 text-primary" />
                <span>Upcoming</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-primary">{upcomingConsultations.length}</div>
              <p className="text-sm text-gray-600">Scheduled consultations</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Users className="h-5 w-5 text-primary" />
                <span>Completed</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-primary">{completedConsultations.length}</div>
              <p className="text-sm text-gray-600">Total consultations</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <DollarSign className="h-5 w-5 text-primary" />
                <span>Earnings</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-primary">₹{(doctorProfile?.total_earnings ?? 0).toFixed(2)}</div>
              <p className="text-sm text-gray-600">20% commission per consultation</p>
            </CardContent>
          </Card>
        </div>

        {/* Consultations List */}
        <Card>
          <CardHeader>
            <CardTitle>All Consultations</CardTitle>
          </CardHeader>
          <CardContent>
            {consultations.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No consultations yet</p>
            ) : (
              <div className="space-y-4">
                {consultations.map((consultation) => (
                  <div key={consultation.id} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="font-semibold">Patient ID: {consultation.user_id.slice(0, 8)}</div>
                        <div className="text-sm text-gray-600">
                          {consultation.slot_date} at {consultation.slot_time}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs bg-primary/10 text-primary px-3 py-1 rounded-full capitalize">
                          {consultation.status}
                        </span>
                        {consultation.status === 'scheduled' && (
                          <Button size="sm" onClick={async () => {
                            setCompleteDialog(consultation);
                            setConsultationDetail(null);
                            setPatientHistory([]);
                            try {
                              const [detailRes, historyRes] = await Promise.all([
                                axios.get(`${API}/consultations/${consultation.id}`, { headers: getAuthHeader() }),
                                axios.get(`${API}/consultations`, { params: { patient_id: consultation.user_id }, headers: getAuthHeader() }),
                              ]);
                              setConsultationDetail(detailRes.data);
                              setPatientHistory((historyRes.data || []).filter((c) => c.id !== consultation.id));
                            } catch {
                              setConsultationDetail(null);
                              setPatientHistory([]);
                            }
                          }} data-testid={`complete-consultation-${consultation.id}`}>
                            <FileText className="h-4 w-4 mr-1" /> Complete
                          </Button>
                        )}
                      </div>
                    </div>
                    {consultation.symptoms && (
                      <div className="text-sm text-gray-600 mt-2">
                        <strong>Symptoms:</strong> {consultation.symptoms}
                      </div>
                    )}
                    {consultation.prescription && (
                      <div className="text-sm bg-gray-50 rounded p-2 mt-2">
                        <strong>Prescription:</strong> {consultation.prescription.notes || JSON.stringify(consultation.prescription)}
                      </div>
                    )}
                    <div className="text-sm text-gray-500 mt-1 capitalize">
                      Type: {consultation.consultation_type}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      <Footer />

      <Dialog open={!!completeDialog} onOpenChange={(open) => !open && (setCompleteDialog(null), setConsultationDetail(null))}>
        <DialogContent data-testid="complete-consultation-dialog" className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Complete consultation</DialogTitle>
          </DialogHeader>
          {completeDialog && (
            <>
              <p className="text-sm text-gray-600">
                {completeDialog.slot_date} at {completeDialog.slot_time} · Patient {completeDialog.user_id?.slice(0, 8)}
              </p>
              <div className="space-y-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Dosha & patient history</p>
                {consultationDetail?.patient_dosha && (
                  <div className="rounded-lg bg-primary/5 border border-primary/20 p-3 space-y-2">
                    <p className="font-medium text-primary">Patient Dosha (for your validation)</p>
                    <p className="text-sm"><strong>Primary:</strong> {consultationDetail.patient_dosha.primary_dosha}</p>
                    <p className="text-sm"><strong>Severity:</strong> {consultationDetail.patient_dosha.imbalance_severity}</p>
                    {consultationDetail.patient_dosha.prakriti && (
                      <p className="text-sm"><strong>Prakriti:</strong> V {consultationDetail.patient_dosha.prakriti.vata} / P {consultationDetail.patient_dosha.prakriti.pitta} / K {consultationDetail.patient_dosha.prakriti.kapha}</p>
                    )}
                    {consultationDetail.patient_dosha.recommendations && (
                      <div className="text-sm">
                        <strong>AI recommendations:</strong>
                        <ul className="list-disc pl-4 mt-1">
                          {Object.entries(consultationDetail.patient_dosha.recommendations).slice(0, 4).map(([k, v]) => (
                            <li key={k}>{k}: {Array.isArray(v) ? v.slice(0, 2).join(', ') : String(v)}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
                {patientHistory.length > 0 && (
                  <div className="rounded-lg bg-muted/50 border p-3">
                    <p className="font-medium mb-2">Previous consultations with this patient</p>
                    <ul className="space-y-1 text-sm">
                      {patientHistory.slice(0, 5).map((c) => (
                        <li key={c.id} className="flex justify-between items-start gap-2">
                          <span>{c.slot_date} — {c.status}</span>
                          <span className="text-gray-600 truncate max-w-[180px]" title={c.prescription?.notes}>
                            {c.prescription?.notes ? `${c.prescription.notes.slice(0, 40)}...` : '—'}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label htmlFor="prescription-notes">Prescription / notes</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={loadingSuggestion}
                    onClick={async () => {
                      if (!completeDialog?.id) return;
                      setLoadingSuggestion(true);
                      try {
                        const res = await axios.post(
                          `${API}/ai/prescription-suggestion`,
                          { consultation_id: completeDialog.id },
                          { headers: getAuthHeader() }
                        );
                        setPrescriptionNotes((prev) => (prev ? prev + '\n\n' : '') + (res.data.suggestion || ''));
                      } catch {
                        toast.error('Could not load suggestion');
                      } finally {
                        setLoadingSuggestion(false);
                      }
                    }}
                  >
                    {loadingSuggestion ? 'Loading...' : 'Get AI suggestion (validate & edit)'}
                  </Button>
                </div>
                <textarea
                  id="prescription-notes"
                  className="w-full border border-gray-200 rounded-lg p-3 min-h-[120px]"
                  placeholder="Herbs, diet, lifestyle advice, follow-up..."
                  value={prescriptionNotes}
                  onChange={(e) => setPrescriptionNotes(e.target.value)}
                />
              </div>
              <div className="flex flex-wrap gap-2 justify-between items-center">
                <Link to="/products">
                  <Button type="button" variant="outline" className="gap-1">
                    <ShoppingBag className="h-4 w-4" /> Buy products for patient
                  </Button>
                </Link>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setCompleteDialog(null)}>Cancel</Button>
                  <Button onClick={handleCompleteConsultation} disabled={submitting}>
                    {submitting ? 'Saving...' : 'Complete consultation'}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DoctorDashboard;